import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { GlobalConstants } from '../global-constants';

export interface TaxLineItem {
  type: 'txn_line_item';
  amount?: number;
  description?: string;
  reference?: string;
  tax_code?: string;
  quantity?: number;
}

export interface TaxCalculationLineItem {
  amount: number;
  reference: string;
  tax_code?: string;
  quantity?: number;
}

export interface CustomerDetails {
  address?: {
    country?: string;
    postal_code?: string;
    state?: string;
    city?: string;
    line1?: string;
    line2?: string;
  };
  email?: string;
  name?: string;
  phone?: string;
  tax_ids?: Array<{
    type: string;
    value: string;
  }>;
}

export interface TaxCalculationRequest {
  line_items: TaxCalculationLineItem[];
  customer_details: CustomerDetails;
  expand?: string[];
}

export interface TaxBreakdown {
  subtotal: number;
  taxAmount: number;
  total: number;
  taxPercent: number;
  jurisdiction?: string;
  taxId?: string;
  estimatedBreakdown?: any;
}

@Injectable({
  providedIn: 'root'
})
export class StripeTaxService {
  private readonly headers = new HttpHeaders({ 'Content-type': 'application/json' });

  constructor(private http: HttpClient) {}

  calculateTripTax(
    amount: number,
    zipCode: string,
    customerId: string,
    tripId?: string
  ): Observable<any> {
    const body = {
      amount,
      zip_code: zipCode,
      customer_id: customerId,
      trip_id: tripId || null
    };

    return this.http.post<any>(
      GlobalConstants.apiURL + 'calculate_trip_tax',
      body,
      { headers: this.headers }
    );
  }

  validateTaxId(
    taxIdType: string,
    taxIdValue: string,
    countryCode: string
  ): Observable<any> {
    const body = {
      tax_id_type: taxIdType,
      tax_id_value: taxIdValue,
      country_code: countryCode
    };

    return this.http.post<any>(
      GlobalConstants.apiURL + 'validate_tax_id',
      body,
      { headers: this.headers }
    );
  }

  getTaxRate(
    zipCode: string,
    countryCode: string = 'US'
  ): Observable<any> {
    const body = {
      zip_code: zipCode,
      country_code: countryCode
    };

    return this.http.post<any>(
      GlobalConstants.apiURL + 'get_tax_rate',
      body,
      { headers: this.headers }
    );
  }

  calculateDetailedTax(
    subtotal: number,
    customerDetails: CustomerDetails,
    description: string = 'Trip Service'
  ): Observable<TaxBreakdown> {
    const body = {
      subtotal,
      customer_details: customerDetails,
      description
    };

    return this.http.post<TaxBreakdown>(
      GlobalConstants.apiURL + 'calculate_detailed_tax',
      body,
      { headers: this.headers }
    );
  }

  // Formats raw tax API data for display in the UI
  formatTaxSummary(taxData: any, knownSubtotal: number = 0): TaxBreakdown {
    // Accept any field name the backend might use for subtotal / tax amount
    const subtotal =
      parseFloat(taxData.subtotal ?? taxData.amount ?? taxData.base_amount ?? knownSubtotal) || knownSubtotal;

    const taxAmount =
      parseFloat(taxData.tax_amount ?? taxData.tax ?? taxData.taxAmount ?? taxData.tax_total ?? 0) || 0;

    const total =
      parseFloat(taxData.total ?? taxData.total_amount ?? (subtotal + taxAmount)) || (subtotal + taxAmount);

    const taxPercent = subtotal > 0
      ? Math.round((taxAmount / subtotal) * 10000) / 100
      : 0;

    return {
      subtotal,
      taxAmount,
      total,
      taxPercent,
      jurisdiction: taxData.jurisdiction ?? taxData.state ?? taxData.location ?? '',
      taxId: taxData.tax_id,
      estimatedBreakdown: taxData.breakdown
    };
  }

  handleTaxError(error: any): TaxBreakdown {
    console.error('Stripe Tax API Error:', error);
    return {
      subtotal: 0,
      taxAmount: 0,
      total: 0,
      taxPercent: 0,
      jurisdiction: 'ERROR'
    };
  }

  normalizeZipCode(zipCode: string, countryCode: string = 'US'): string {
    if (!zipCode) return '';

    switch (countryCode.toUpperCase()) {
      case 'US':
        // US format: 12345 or 12345-6789
        return zipCode.replace(/[^0-9\-]/g, '');
      case 'CA':
        // Canada format: A1A 1A1
        return zipCode.toUpperCase().replace(/[^A-Z0-9\s]/g, '');
      case 'GB':
        // UK format: AA9A 9AA
        return zipCode.toUpperCase().replace(/[^A-Z0-9\s]/g, '');
      default:
        return zipCode;
    }
  }

  isValidZipFormat(zipCode: string, countryCode: string = 'US'): boolean {
    if (!zipCode) return false;

    switch (countryCode.toUpperCase()) {
      case 'US':
        return /^\d{5}(-\d{4})?$/.test(zipCode);
      case 'CA':
        return /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i.test(zipCode);
      case 'GB':
        return /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(zipCode);
      default:
        return zipCode.length >= 3;
    }
  }

  estimateRecurringTax(
    monthlyAmount: number,
    zipCode: string
  ): Observable<any> {
    const body = {
      monthly_amount: monthlyAmount,
      zip_code: zipCode,
      recurring: true
    };

    return this.http.post<any>(
      GlobalConstants.apiURL + 'estimate_recurring_tax',
      body,
      { headers: this.headers }
    );
  }
}
